import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchNonconformanceReport } from '../../api/nonconformanceReport';
import { fetchQcAnalyticsNcrs } from '../../api/qcAnalytics';
import { PRODUCT_SUB_NCR } from '../layout/Header';
import NcrPreviewView from '../approval/previews/NcrPreviewView';
import {
  ncrCauseCategoryLabel,
  ncrDefectCategoryLabel,
  ncrDispositionLabel,
  ncrSourceLabel,
  ncrStatusLabel,
  NonconformanceReport,
} from '../../types/nonconformanceReport';
import { QcNcrDrillDimension, QcNcrDrillQuery } from '../../types/qcAnalytics';
import { notifyError } from '../../utils/notify';
import '../product/PreviousInspectionPickerModal.css';
import '../product/NonconformanceReportPanel.css';

export type QcNcrDrillTarget = {
  title: string;
  subtitle?: string;
  dimension?: QcNcrDrillDimension | null;
  key?: string | null;
};

type Props = {
  open: boolean;
  target: QcNcrDrillTarget | null;
  query: Pick<QcNcrDrillQuery, 'from' | 'to' | 'contractId'>;
  onClose: () => void;
};

function apiErrorMessage(err: unknown, fallback: string) {
  const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return message?.trim() || fallback;
}

function statusText(row: NonconformanceReport) {
  return ncrStatusLabel(row.status, row.approvalInProgress);
}

export default function QcNcrDrillModal({ open, target, query, onClose }: Props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<NonconformanceReport[]>([]);
  const [keyword, setKeyword] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<NonconformanceReport | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setKeyword('');
      setRows([]);
      setSelectedId(null);
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSelectedId(null);
    setDetail(null);
    void fetchQcAnalyticsNcrs({
      from: query.from,
      to: query.to,
      contractId: query.contractId,
      dimension: target?.dimension,
      key: target?.key,
    })
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch((err) => {
        if (!cancelled) {
          setRows([]);
          notifyError(apiErrorMessage(err, 'NCR 목록을 불러오지 못했습니다.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, target?.dimension, target?.key, target?.title, query.from, query.to, query.contractId]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    void fetchNonconformanceReport(selectedId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setDetail(null);
          notifyError(apiErrorMessage(err, 'NCR 상세를 불러오지 못했습니다.'));
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const hay = [
        row.reportNo,
        row.itemName,
        row.lotNo,
        row.defectCategory,
        row.causeCategory,
        row.sourceType,
        row.disposition,
        row.defectDesc,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, keyword]);

  const openInNcrMenu = () => {
    if (!selectedId) return;
    onClose();
    navigate('/app', {
      state: { activeMenu: PRODUCT_SUB_NCR, ncrReportId: selectedId },
    });
  };

  if (!open || !target) return null;

  return (
    <div className="prev-insp-modal__backdrop" onClick={onClose} role="presentation">
      <div
        className="prev-insp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="qc-ncr-drill-title"
        onClick={(e) => e.stopPropagation()}
        style={{ width: selectedId ? 'min(1100px, 100%)' : 'min(980px, 100%)' }}
      >
        <div className="prev-insp-modal__head">
          <div>
            <p className="prev-insp-modal__eyebrow">QC 분석 · NCR</p>
            <h3 id="qc-ncr-drill-title">
              {selectedId && detail
                ? detail.reportNo || detail.itemName || 'NCR 상세'
                : target.title}
            </h3>
            <p className="prev-insp-modal__sub">
              {selectedId
                ? '행을 선택하면 상세를 확인합니다. 수정·결재는 부적합 메뉴에서 열 수 있습니다.'
                : target.subtitle ||
                  `${query.from || '-'} ~ ${query.to || '-'} 기간의 해당 NCR 목록입니다.`}
            </p>
          </div>
          <button type="button" className="prev-insp-modal__close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>

        {selectedId ? (
          <div className="prev-insp-modal__toolbar">
            <button
              type="button"
              className="prev-insp-modal__pager-btn"
              onClick={() => setSelectedId(null)}
            >
              ← 목록
            </button>
            <button
              type="button"
              className="prev-insp-modal__pick"
              onClick={openInNcrMenu}
              disabled={detailLoading || !detail}
            >
              부적합 메뉴에서 열기
            </button>
            <span className="prev-insp-modal__count">
              {detailLoading ? '상세 불러오는 중…' : detail?.reportNo || `#${selectedId}`}
            </span>
          </div>
        ) : (
          <div className="prev-insp-modal__toolbar">
            <input
              className="prev-insp-modal__search"
              type="search"
              placeholder="번호·품명·유형 검색"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            <span className="prev-insp-modal__count">
              {loading ? '조회 중…' : `${filtered.length}건`}
            </span>
          </div>
        )}

        <div className="prev-insp-modal__body">
          {selectedId ? (
            detailLoading ? (
              <p className="prev-insp-modal__empty">상세 불러오는 중…</p>
            ) : detail ? (
              <div style={{ padding: '8px 16px 16px' }}>
                <NcrPreviewView detail={detail} />
              </div>
            ) : (
              <p className="prev-insp-modal__empty">상세를 불러오지 못했습니다.</p>
            )
          ) : loading ? (
            <p className="prev-insp-modal__empty">불러오는 중…</p>
          ) : filtered.length === 0 ? (
            <p className="prev-insp-modal__empty">해당 조건의 NCR이 없습니다.</p>
          ) : (
            <div className="prev-insp-modal__table-wrap">
              <table className="prev-insp-modal__table">
                <thead>
                  <tr>
                    <th>번호</th>
                    <th>품명</th>
                    <th>발생원</th>
                    <th>불량유형</th>
                    <th>원인</th>
                    <th>조치</th>
                    <th>발생일</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr
                      key={row.reportId}
                      onClick={() => setSelectedId(row.reportId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedId(row.reportId);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                    >
                      <td>{row.reportNo || '-'}</td>
                      <td>{row.itemName || '-'}</td>
                      <td>{ncrSourceLabel(row.sourceType)}</td>
                      <td>{ncrDefectCategoryLabel(row.defectCategory)}</td>
                      <td>{ncrCauseCategoryLabel(row.causeCategory)}</td>
                      <td>{ncrDispositionLabel(row.disposition)}</td>
                      <td>{row.reportDate || '-'}</td>
                      <td>{statusText(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
